import { getDb } from "@/services/storage/sqlite/connection";
import { createId } from "@/utils";
import type { Note, NoteAttachment, NoteFolder, NoteLink, NoteSummary, NoteVersion } from "@/types";
import { NOTE_VERSION_LIMIT } from "@/types";

interface NoteRow {
  id: string;
  workspace_id: string;
  folder_id: string | null;
  title: string;
  content: string;
  tags_json: string;
  pinned: number;
  created_at: number;
  updated_at: number;
}

interface NoteFolderRow {
  id: string;
  workspace_id: string;
  parent_id: string | null;
  name: string;
  created_at: number;
}

interface NoteVersionRow {
  id: string;
  note_id: string;
  content: string;
  created_at: number;
}

interface NoteLinkRow {
  id: string;
  from_note_id: string;
  to_note_id: string;
  created_at: number;
}

interface NoteAttachmentRow {
  id: string;
  note_id: string;
  file_name: string;
  file_path: string;
  mime_type: string;
  created_at: number;
}

function rowToNote(row: NoteRow): Note {
  return {
    id: row.id,
    workspaceId: row.workspace_id,
    folderId: row.folder_id,
    title: row.title,
    content: row.content,
    tags: JSON.parse(row.tags_json) as string[],
    pinned: row.pinned === 1,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function excerptOf(content: string): string {
  const flat = content.replace(/\s+/g, " ").trim();
  return flat.length > 140 ? `${flat.slice(0, 140)}…` : flat;
}

function rowToSummary(row: NoteRow): NoteSummary {
  return {
    id: row.id,
    workspaceId: row.workspace_id,
    folderId: row.folder_id,
    title: row.title,
    excerpt: excerptOf(row.content),
    tags: JSON.parse(row.tags_json) as string[],
    pinned: row.pinned === 1,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function rowToFolder(row: NoteFolderRow): NoteFolder {
  return { id: row.id, workspaceId: row.workspace_id, parentId: row.parent_id, name: row.name, createdAt: row.created_at };
}

function rowToVersion(row: NoteVersionRow): NoteVersion {
  return { id: row.id, noteId: row.note_id, content: row.content, createdAt: row.created_at };
}

function rowToLink(row: NoteLinkRow): NoteLink {
  return { id: row.id, fromNoteId: row.from_note_id, toNoteId: row.to_note_id, createdAt: row.created_at };
}

function rowToAttachment(row: NoteAttachmentRow): NoteAttachment {
  return {
    id: row.id,
    noteId: row.note_id,
    fileName: row.file_name,
    filePath: row.file_path,
    mimeType: row.mime_type,
    createdAt: row.created_at,
  };
}

export const notesRepository = {
  async listSummaries(workspaceId: string): Promise<NoteSummary[]> {
    const db = await getDb();
    const rows = await db.select<NoteRow[]>(
      "SELECT * FROM notes WHERE workspace_id = $1 ORDER BY pinned DESC, updated_at DESC",
      [workspaceId],
    );
    return rows.map(rowToSummary);
  },

  async get(id: string): Promise<Note | null> {
    const db = await getDb();
    const rows = await db.select<NoteRow[]>("SELECT * FROM notes WHERE id = $1", [id]);
    return rows[0] ? rowToNote(rows[0]) : null;
  },

  async create(input: { workspaceId: string; title: string; folderId?: string | null }): Promise<Note> {
    const db = await getDb();
    const now = Date.now();
    const note: Note = {
      id: createId("note"),
      workspaceId: input.workspaceId,
      folderId: input.folderId ?? null,
      title: input.title,
      content: "",
      tags: [],
      pinned: false,
      createdAt: now,
      updatedAt: now,
    };
    await db.execute(
      `INSERT INTO notes (id, workspace_id, folder_id, title, content, tags_json, pinned, created_at, updated_at)
       VALUES ($1, $2, $3, $4, $5, $6, 0, $7, $8)`,
      [note.id, note.workspaceId, note.folderId, note.title, note.content, JSON.stringify(note.tags), now, now],
    );
    return note;
  },

  /** Updates title/content and snapshots the previous content into
   * `note_versions`, pruning anything beyond `NOTE_VERSION_LIMIT`. */
  async save(id: string, patch: { title?: string; content?: string; tags?: string[] }): Promise<void> {
    const db = await getDb();
    const existing = await this.get(id);
    if (!existing) return;
    if (patch.content !== undefined && patch.content !== existing.content) {
      await db.execute("INSERT INTO note_versions (id, note_id, content, created_at) VALUES ($1, $2, $3, $4)", [
        createId("nver"),
        id,
        existing.content,
        Date.now(),
      ]);
      await db.execute(
        `DELETE FROM note_versions WHERE note_id = $1 AND id NOT IN (
           SELECT id FROM note_versions WHERE note_id = $1 ORDER BY created_at DESC LIMIT $2
         )`,
        [id, NOTE_VERSION_LIMIT],
      );
    }
    const title = patch.title ?? existing.title;
    const content = patch.content ?? existing.content;
    const tags = patch.tags ?? existing.tags;
    await db.execute(
      "UPDATE notes SET title = $1, content = $2, tags_json = $3, updated_at = $4 WHERE id = $5",
      [title, content, JSON.stringify(tags), Date.now(), id],
    );
  },

  async setPinned(id: string, pinned: boolean): Promise<void> {
    const db = await getDb();
    await db.execute("UPDATE notes SET pinned = $1, updated_at = $2 WHERE id = $3", [pinned ? 1 : 0, Date.now(), id]);
  },

  async setFolder(id: string, folderId: string | null): Promise<void> {
    const db = await getDb();
    await db.execute("UPDATE notes SET folder_id = $1, updated_at = $2 WHERE id = $3", [folderId, Date.now(), id]);
  },

  async delete(id: string): Promise<void> {
    const db = await getDb();
    await db.execute("DELETE FROM notes WHERE id = $1", [id]);
  },

  // --- Folders -----------------------------------------------------------

  async listFolders(workspaceId: string): Promise<NoteFolder[]> {
    const db = await getDb();
    const rows = await db.select<NoteFolderRow[]>(
      "SELECT * FROM note_folders WHERE workspace_id = $1 ORDER BY name ASC",
      [workspaceId],
    );
    return rows.map(rowToFolder);
  },

  async createFolder(workspaceId: string, name: string, parentId: string | null = null): Promise<NoteFolder> {
    const db = await getDb();
    const folder: NoteFolder = { id: createId("nfld"), workspaceId, parentId, name, createdAt: Date.now() };
    await db.execute(
      "INSERT INTO note_folders (id, workspace_id, parent_id, name, created_at) VALUES ($1, $2, $3, $4, $5)",
      [folder.id, folder.workspaceId, folder.parentId, folder.name, folder.createdAt],
    );
    return folder;
  },

  async deleteFolder(id: string): Promise<void> {
    const db = await getDb();
    await db.execute("DELETE FROM note_folders WHERE id = $1", [id]);
  },

  // --- Versions ------------------------------------------------------------

  async listVersions(noteId: string): Promise<NoteVersion[]> {
    const db = await getDb();
    const rows = await db.select<NoteVersionRow[]>(
      "SELECT * FROM note_versions WHERE note_id = $1 ORDER BY created_at DESC",
      [noteId],
    );
    return rows.map(rowToVersion);
  },

  async restoreVersion(noteId: string, versionId: string): Promise<void> {
    const db = await getDb();
    const rows = await db.select<NoteVersionRow[]>("SELECT * FROM note_versions WHERE id = $1", [versionId]);
    const version = rows[0];
    if (!version) return;
    await this.save(noteId, { content: version.content });
  },

  // --- Links ---------------------------------------------------------------

  async listLinks(noteId: string): Promise<NoteLink[]> {
    const db = await getDb();
    const rows = await db.select<NoteLinkRow[]>(
      "SELECT * FROM note_links WHERE from_note_id = $1 OR to_note_id = $1",
      [noteId],
    );
    return rows.map(rowToLink);
  },

  async link(fromNoteId: string, toNoteId: string): Promise<void> {
    const db = await getDb();
    await db.execute("INSERT INTO note_links (id, from_note_id, to_note_id, created_at) VALUES ($1, $2, $3, $4)", [
      createId("nlnk"),
      fromNoteId,
      toNoteId,
      Date.now(),
    ]);
  },

  async unlink(linkId: string): Promise<void> {
    const db = await getDb();
    await db.execute("DELETE FROM note_links WHERE id = $1", [linkId]);
  },

  // --- Attachments -----------------------------------------------------------

  async listAttachments(noteId: string): Promise<NoteAttachment[]> {
    const db = await getDb();
    const rows = await db.select<NoteAttachmentRow[]>("SELECT * FROM note_attachments WHERE note_id = $1", [noteId]);
    return rows.map(rowToAttachment);
  },

  async addAttachment(input: {
    noteId: string;
    fileName: string;
    filePath: string;
    mimeType: string;
  }): Promise<NoteAttachment> {
    const db = await getDb();
    const attachment: NoteAttachment = { id: createId("natt"), createdAt: Date.now(), ...input };
    await db.execute(
      `INSERT INTO note_attachments (id, note_id, file_name, file_path, mime_type, created_at)
       VALUES ($1, $2, $3, $4, $5, $6)`,
      [attachment.id, attachment.noteId, attachment.fileName, attachment.filePath, attachment.mimeType, attachment.createdAt],
    );
    return attachment;
  },

  async removeAttachment(id: string): Promise<void> {
    const db = await getDb();
    await db.execute("DELETE FROM note_attachments WHERE id = $1", [id]);
  },
};
